import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useChampionship } from "@/context/ChampionshipContext";
import { api, formatApiError, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { COUNTRIES } from "@/lib/countries";
import { FileDown } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const { current } = useChampionship() || {};
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", country_code: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [assets, setAssets] = useState({});

  useEffect(() => {
    if (!current?.id) return;
    api.get(`/championships/${current.id}/site-assets`).then((r) => setAssets(r.data || {})).catch(() => {});
  }, [current?.id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const country = COUNTRIES.find((c) => c.code === form.country_code);
      await register({ ...form, country_name: country?.name || form.country_code });
      navigate("/captain", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="register-page">
      <div className="text-[11px] uppercase tracking-[0.3em] text-primary">Captain registration</div>
      <h1 className="font-display text-4xl font-extrabold tracking-tighter mt-3">Register your federation.</h1>
      <p className="mt-2 text-muted-foreground">
        Sign up to enter your country's team. Your account will be activated after approval by the WOC organising committee.
      </p>

      {(assets.registration_pdf_path || assets.rules_pdf_path) && (
        <div className="mt-8 border border-border bg-secondary/50 p-5">
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
            Before you register — please read
          </div>
          <div className="flex flex-col gap-2">
            {assets.registration_pdf_path && (
              <a
                href={imageUrl(assets.registration_pdf_path)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-3 text-sm font-medium hover:text-primary group"
                data-testid="download-registration-pdf"
              >
                <FileDown className="h-4 w-4 text-primary" />
                <span className="underline underline-offset-4">
                  {assets.registration_pdf_name || "Registration details (PDF)"}
                </span>
                <span className="text-xs text-muted-foreground group-hover:text-primary">Download ↓</span>
              </a>
            )}
            {assets.rules_pdf_path && (
              <a
                href={imageUrl(assets.rules_pdf_path)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-3 text-sm font-medium hover:text-primary group"
                data-testid="download-rules-pdf"
              >
                <FileDown className="h-4 w-4 text-primary" />
                <span className="underline underline-offset-4">
                  {assets.rules_pdf_name || "WOC tournament rules (PDF)"}
                </span>
                <span className="text-xs text-muted-foreground group-hover:text-primary">Download ↓</span>
              </a>
            )}
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-10 space-y-5">
        <div className="space-y-2">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" value={form.name} onChange={set("name")} required data-testid="register-name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="country">Country</Label>
          <Select value={form.country_code} onValueChange={set("country_code")}>
            <SelectTrigger data-testid="register-country">
              <SelectValue placeholder="Select your country" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`register-country-${c.code}`}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="register-email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" minLength={6} value={form.password} onChange={set("password")} required data-testid="register-password" />
        </div>
        {error && <div className="text-sm text-destructive" data-testid="register-error">{error}</div>}
        <Button type="submit" disabled={busy || !form.country_code} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="register-submit">
          {busy ? "Creating account…" : "Create account"}
        </Button>
      </form>

      <div className="mt-8 text-sm text-muted-foreground">
        Already registered? <Link to="/login" className="text-primary underline">Sign in</Link>.
      </div>
    </div>
  );
}
